import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link, Plus, Trash2, Pencil, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Searchable Select Component
function SearchableSelect({ options, value, onChange, placeholder, searchPlaceholder, renderOption, getOptionValue, getOptionLabel, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(opt => 
      getOptionLabel(opt).toLowerCase().includes(searchLower)
    );
  }, [options, search, getOptionLabel]);

  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2 text-left flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-zinc-600'}`}
        disabled={disabled}
      >
        <span className={selectedOption ? 'text-white' : 'text-zinc-400'}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-zinc-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder || "Search..."}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded px-8 py-1.5 text-sm focus:outline-none focus:border-zinc-500"
                autoFocus
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-zinc-400 hover:text-white" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-zinc-400 text-center">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={getOptionValue(option)}
                  type="button"
                  onClick={() => {
                    onChange(getOptionValue(option));
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-800 ${
                    getOptionValue(option) === value ? 'bg-zinc-800 text-white' : 'text-zinc-300'
                  }`}
                >
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearch('');
          }}
        />
      )}
    </div>
  );
}

export default function LinkMappings() {
  const [mappings, setMappings] = useState([]);
  const [vas, setVas] = useState([]);
  const [availableLinks, setAvailableLinks] = useState([]);
  const [cupidBotModels, setCupidBotModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [selectedVA, setSelectedVA] = useState('');
  const [selectedLink, setSelectedLink] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mappingsRes, vasRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/links/mappings`),
        axios.get(`${API_URL}/vas`),
        axios.get(`${API_URL}/settings`),
      ]);
      setMappings(mappingsRes.data);
      setVas(vasRes.data);
      setCupidBotModels(settingsRes.data.cupidbot_tracked_models || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableLinks = async () => {
    setLoadingLinks(true);
    try {
      const response = await axios.get(`${API_URL}/links/available`);
      setAvailableLinks(response.data.links || []);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Failed to fetch links from GetMySocial');
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleCreateMapping = async () => {
    if (!selectedVA || !selectedLink) {
      toast.error('Please select both VA and Link');
      return;
    }

    setCreating(true);
    try {
      const va = vas.find((v) => v.va_id === selectedVA);
      const link = availableLinks.find((l) => l._id === selectedLink);
      const model = cupidBotModels.find((m) => m.id === selectedModel);

      if (!va || !link) {
        toast.error('Selected VA or Link not found');
        setCreating(false);
        return;
      }

      await axios.post(`${API_URL}/links/mappings`, {
        link_id: link._id,
        link_name: link.displayName || link.originalLink,
        va_id: va.va_id,
        va_name: va.name,
        model_id: model?.id || null,
        model_name: model?.name || null,
      });

      toast.success('Link mapping created successfully');
      setOpenCreate(false);
      setSelectedVA('');
      setSelectedLink('');
      setSelectedModel('');
      fetchData();
    } catch (error) {
      console.error('Error creating mapping:', error);
      toast.error(error.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (mapping) => {
    setEditMapping(mapping);
    setSelectedVA(mapping.va_id);
    setSelectedLink(mapping.link_id);
    setSelectedModel(mapping.model_id || '');
    setOpenEdit(true);
    if (availableLinks.length === 0) {
      fetchAvailableLinks();
    }
  };

  const handleUpdateMapping = async () => {
    if (!selectedVA) {
      toast.error('Please select a VA');
      return;
    }

    setUpdating(true);
    try {
      const va = vas.find((v) => v.va_id === selectedVA);
      const model = cupidBotModels.find((m) => m.id === selectedModel);
      
      const updateData = {
        va_id: selectedVA,
        model_id: model?.id || null,
        model_name: model?.name || null,
      };

      // If link changed, include the new link
      if (selectedLink !== editMapping.link_id) {
        const link = availableLinks.find((l) => l._id === selectedLink);
        if (link) {
          updateData.link_id = link._id;
        }
      }

      await axios.put(`${API_URL}/links/mappings/${editMapping.link_id}`, updateData);

      // Update local state with new names
      const updatedMappings = mappings.map(m => {
        if (m.link_id === editMapping.link_id) {
          return {
            ...m,
            va_id: selectedVA,
            va_name: va?.name || m.va_name,
            link_id: selectedLink,
            model_id: model?.id || null,
            model_name: model?.name || null,
          };
        }
        return m;
      });
      setMappings(updatedMappings);

      toast.success('Mapping updated successfully');
      setOpenEdit(false);
      setEditMapping(null);
      setSelectedModel('');
      fetchData(); // Refresh to get updated data
    } catch (error) {
      console.error('Error updating mapping:', error);
      toast.error(error.response?.data?.detail || 'Failed to update mapping');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteMapping = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;

    try {
      await axios.delete(`${API_URL}/links/mappings/${linkId}`);
      toast.success('Mapping deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error('Failed to delete mapping');
    }
  };

  // Filter mappings based on search
  const filteredMappings = useMemo(() => {
    if (!searchQuery) return mappings;
    const query = searchQuery.toLowerCase();
    return mappings.filter(m => 
      m.link_name?.toLowerCase().includes(query) ||
      m.va_name?.toLowerCase().includes(query) ||
      m.link_id?.toLowerCase().includes(query)
    );
  }, [mappings, searchQuery]);

  // Filter out already mapped links from available links
  const unmappedLinks = useMemo(() => {
    const mappedLinkIds = new Set(mappings.map(m => m.link_id));
    return availableLinks.filter(link => !mappedLinkIds.has(link._id));
  }, [availableLinks, mappings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="link-mappings-title">
            Link Mappings
          </h1>
          <p className="text-base text-muted-foreground mt-2">Associate GetMySocial links with VAs</p>
        </div>
        <Dialog open={openCreate} onOpenChange={(open) => {
          setOpenCreate(open);
          if (open && availableLinks.length === 0) {
            fetchAvailableLinks();
          }
          if (!open) {
            setSelectedVA('');
            setSelectedLink('');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-white text-black hover:bg-zinc-200" data-testid="create-mapping-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Mapping
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Create Link Mapping</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Associate a GetMySocial link with a VA
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Virtual Assistant</Label>
                <SearchableSelect
                  options={vas}
                  value={selectedVA}
                  onChange={setSelectedVA}
                  placeholder="Select VA"
                  searchPlaceholder="Search by name or email..."
                  getOptionValue={(va) => va.va_id}
                  getOptionLabel={(va) => `${va.name} (${va.email})`}
                  renderOption={(va) => (
                    <div>
                      <div className="font-medium">{va.name}</div>
                      <div className="text-xs text-zinc-400">{va.email}</div>
                    </div>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Link</Label>
                {loadingLinks ? (
                  <div className="text-sm text-zinc-400 py-2">Loading links from GetMySocial...</div>
                ) : unmappedLinks.length === 0 && availableLinks.length > 0 ? (
                  <div className="text-sm text-amber-400 py-2">All links are already mapped</div>
                ) : (
                  <SearchableSelect
                    options={unmappedLinks}
                    value={selectedLink}
                    onChange={setSelectedLink}
                    placeholder="Select Link"
                    searchPlaceholder="Search by link name..."
                    getOptionValue={(link) => link._id}
                    getOptionLabel={(link) => link.displayName || link.originalLink}
                    renderOption={(link) => (
                      <div>
                        <div className="font-medium truncate">{link.displayName || link.originalLink}</div>
                        <div className="text-xs text-zinc-400 font-mono">{link._id.substring(0, 12)}...</div>
                      </div>
                    )}
                  />
                )}
                {!loadingLinks && unmappedLinks.length > 0 && (
                  <p className="text-xs text-zinc-500">{unmappedLinks.length} link(s) available</p>
                )}
              </div>

              {/* CupidBot Model Selector */}
              {cupidBotModels.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white">CupidBot Model (Optional)</Label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2"
                    data-testid="select-model-create"
                  >
                    <option value="">No Model</option>
                    {cupidBotModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-zinc-500">Associate this link with a CupidBot model for analytics</p>
                </div>
              )}

              <Button
                onClick={handleCreateMapping}
                className="w-full bg-white text-black hover:bg-zinc-200"
                disabled={creating || !selectedVA || !selectedLink}
                data-testid="create-mapping-submit"
              >
                {creating ? 'Creating...' : 'Create Mapping'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search bar for mappings */}
      {mappings.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by link name or VA name..."
            className="pl-10 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500"
            data-testid="search-mappings"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4 text-zinc-400 hover:text-white" />
            </button>
          )}
        </div>
      )}

      {mappings.length === 0 ? (
        <div className="bg-card p-12 rounded-md border border-border text-center">
          <Link className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No link mappings yet</h3>
          <p className="text-muted-foreground">Create a mapping to start tracking VA performance</p>
        </div>
      ) : filteredMappings.length === 0 ? (
        <div className="bg-card p-8 rounded-md border border-border text-center">
          <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No mappings found</h3>
          <p className="text-muted-foreground">Try a different search term</p>
        </div>
      ) : (
        <div className="bg-card rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Link Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Link ID</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">VA Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Model</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMappings.map((mapping) => (
                <TableRow
                  key={mapping.link_id}
                  className="border-b border-border table-row-hover"
                  data-testid={`mapping-row-${mapping.link_id}`}
                >
                  <TableCell className="font-medium text-white">{mapping.link_name}</TableCell>
                  <TableCell className="font-mono text-sm text-zinc-400">{mapping.link_id}</TableCell>
                  <TableCell className="text-white">{mapping.va_name}</TableCell>
                  <TableCell>
                    {mapping.model_name ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                        {mapping.model_name}
                      </span>
                    ) : (
                      <span className="text-zinc-500 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(mapping)}
                        className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                        data-testid={`edit-mapping-${mapping.link_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMapping(mapping.link_id)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                        data-testid={`delete-mapping-${mapping.link_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={openEdit} onOpenChange={(open) => {
        setOpenEdit(open);
        if (!open) {
          setEditMapping(null);
          setSelectedVA('');
          setSelectedLink('');
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Link Mapping</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update the VA or link for this mapping
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Virtual Assistant</Label>
              <SearchableSelect
                options={vas}
                value={selectedVA}
                onChange={setSelectedVA}
                placeholder="Select VA"
                searchPlaceholder="Search by name or email..."
                getOptionValue={(va) => va.va_id}
                getOptionLabel={(va) => `${va.name} (${va.email})`}
                renderOption={(va) => (
                  <div>
                    <div className="font-medium">{va.name}</div>
                    <div className="text-xs text-zinc-400">{va.email}</div>
                  </div>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Link</Label>
              {loadingLinks ? (
                <div className="text-sm text-zinc-400 py-2">Loading links...</div>
              ) : (
                <SearchableSelect
                  options={availableLinks}
                  value={selectedLink}
                  onChange={setSelectedLink}
                  placeholder="Select Link"
                  searchPlaceholder="Search by link name..."
                  getOptionValue={(link) => link._id}
                  getOptionLabel={(link) => link.displayName || link.originalLink}
                  renderOption={(link) => (
                    <div>
                      <div className="font-medium truncate">{link.displayName || link.originalLink}</div>
                      <div className="text-xs text-zinc-400 font-mono">{link._id.substring(0, 12)}...</div>
                    </div>
                  )}
                />
              )}
            </div>

            {/* CupidBot Model Selector */}
            {cupidBotModels.length > 0 && (
              <div className="space-y-2">
                <Label className="text-white">CupidBot Model</Label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2"
                  data-testid="select-model-edit"
                >
                  <option value="">No Model</option>
                  {cupidBotModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={() => setOpenEdit(false)}
                variant="outline"
                className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateMapping}
                className="flex-1 bg-white text-black hover:bg-zinc-200"
                disabled={updating || !selectedVA}
                data-testid="update-mapping-submit"
              >
                {updating ? 'Updating...' : 'Update Mapping'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
